/**
 * railLogic — the lead rail's decisions, kept out of the component (D-4).
 *
 * D-4 collapses the lead page's right-hand column into ONE rail: the texting
 * agent, the five next-step cards, the screening surface and the profile, all
 * reached from a single four-tab strip instead of a rail beside a card beside a
 * banner. Three of that rail's rules are worth testing on their own, so they
 * live here rather than inside the JSX:
 *
 *   - which `?tab=` values still land somewhere. Escalation popups, readiness
 *     jump-links, the console's "open the lead" links and older bookmarks all
 *     deep-link with the OLD three names, and C-20 says every one of them keeps
 *     working — a rail that only understood its new vocabulary would silently
 *     dump all of them on Next steps.
 *   - whether the Agent tab exists at all for this lead. A lead with no agent
 *     surface must not get a dead tab; a lead whose presence has not reported
 *     yet must not have a tab appear underneath the recruiter's finger.
 *   - whether that tab wears the attention dot.
 *
 * The escalation kind sets are NOT re-declared here. `visibleAttentionEscalations`
 * (lead/attentionSummary.ts) is the canonical filter and the AttentionStrip
 * counts its flags through the very same call, so the tab dot and the strip line
 * can never disagree about what a flag is.
 */
import type { AgentThreadState } from '../../AgentPanel';
import { visibleAttentionEscalations } from '../attentionSummary';

/** The rail's four panels. 'agent' is new in D-4; the other three are the tab
 *  names ContactRail already published as `?tab=` deep links. */
export type RailTab = 'agent' | 'next' | 'screening' | 'profile';

/** What AgentPanel reports up about this lead's agent surface (its `onPresence`
 *  payload, mirrored here so the rail never imports the panel's internals). */
export interface AgentPresence {
  /** The panel has finished its first fetch. Before that, nothing is known. */
  loaded: boolean;
  /** An OPEN agent conversation exists on this lead. */
  hasConversation: boolean;
  /** No conversation, but the study is agent-enabled — "Hand to agent" shows. */
  canOffer: boolean;
}

/**
 * `?tab=` → the rail tab it names, or null when the value is not a rail tab at
 * all (the caller decides the default; today `?? 'next'`). Written as a switch
 * so TypeScript narrows the literal itself — no cast, and adding a fifth tab
 * cannot silently widen this to `string`.
 */
export function resolveRailTab(urlTab: string | null): RailTab | null {
  // `next`, `screening` and `profile` are the three pre-D-4 deep-link values,
  // unchanged (C-20); `agent` is new, now that the agent lives in the rail.
  switch (urlTab) {
    case 'next':
    case 'screening':
    case 'profile':
    case 'agent':
      return urlTab;
    default:
      return null;
  }
}

/**
 * Does this lead get an Agent tab?
 *
 * While presence is unknown (not reported yet, or reported mid-load) the tab
 * STAYS: AgentPanel reports `loaded: false` on every contact change, and a tab
 * that vanished and came back on each navigation would move the other three
 * under the pointer. Once the panel has actually looked, the tab survives only
 * if there is something behind it — an open conversation, or the Hand-to-agent
 * offer.
 */
export function showAgentTab(p: AgentPresence | null): boolean {
  if (!p || !p.loaded) return true;
  return p.hasConversation || p.canOffer;
}

/**
 * The attention dot on the Agent tab: something on the agent thread is waiting
 * on a human, and the recruiter cannot see it because another tab is open.
 *
 * Three causes, all of them a decision somebody owes:
 *  - a reply sitting in `drafted` (or guardrail-`suppressed`, which is the same
 *    wait with a warning on it) — `draftPending` covers only the former, so the
 *    dot reads the list too,
 *  - a visible open/claimed escalation — filtered through the CANONICAL filter,
 *    so the ops kinds (`draft_wait`, `dormant_handback`) that the strip refuses
 *    to call flags cannot light this dot either,
 *  - `awaiting_review`: the screen finished and the eligibility determination is
 *    the next action on the page.
 */
export function agentTabAttention(t: AgentThreadState | null): boolean {
  if (!t) return false;
  if (t.draftPending || t.drafts.length > 0) return true;
  if (t.status === 'awaiting_review') return true;
  return visibleAttentionEscalations(t.escalations).length > 0;
}

/**
 * Below `lg` the rail is a bottom sheet (D-12): collapsed to a peek strip —
 * drag handle + the pinned agent status line, so Take over stays reachable
 * with the sheet closed — and expanded to the tabs + panels. Whether the
 * sheet STARTS expanded is a deep-link question: a URL that names a rail tab
 * (`?tab=screening` from an escalation popup or a readiness jump-link) is
 * asking for that tab to be visible, and on a phone "visible" means the
 * sheet is open. No named tab → closed, the thread owns the screen.
 */
export function initialSheetExpanded(urlTab: string | null): boolean {
  return resolveRailTab(urlTab) !== null;
}

/** Minimum vertical travel (px) before a touch counts as a swipe, not a tap. */
export const SHEET_SWIPE_THRESHOLD = 30;

/**
 * Classify a touch gesture on the sheet handle. `startY`/`currentY` are
 * clientY values (screen px, larger = lower); a finger moving UP (positive
 * delta) on a collapsed sheet expands it, moving DOWN on an expanded sheet
 * collapses it. Anything under the threshold is a tap and returns null —
 * the handle's onClick owns taps.
 */
export function sheetSwipeAction(
  startY: number,
  currentY: number,
  expanded: boolean
): 'expand' | 'collapse' | null {
  const delta = startY - currentY;
  if (!expanded && delta > SHEET_SWIPE_THRESHOLD) return 'expand';
  if (expanded && delta < -SHEET_SWIPE_THRESHOLD) return 'collapse';
  return null;
}
